import { useQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';

import useIsAssignmentReady from '@glass/common/modules/assignments/useIsAssignmentReady';
import { SEED_QUERY } from '@glass/common/modules/pages/queryParams';
import { SPACE_STRING } from '@glass/common/modules/strings/constants';
import { RANDOM_RESUME_SECTION_ID } from '@glass/web/modules/content/pages/contentIds';
import useResumeFontLoader from '@glass/web/modules/fonts/useResumeFontLoader';
import useIsDocumentReadyPriorityLoadedIdleInView from '@glass/web/modules/loading/useIsDocumentReadyPriorityLoadedIdleInView';
import {
  FONT_STYLE_FIELD,
  RESUME_V2_FIELD,
  TEMPLATE_FIELD_V2,
} from '@glass/web/modules/resumes/fieldKeys';

import Link from '@glass/shared/components/Link/Link';
import RandomResume from '@glass/shared/components/Section/RandomResumeSection/RandomResume';
import Section from '@glass/shared/components/Section/Section';
import useBatchedPageSeeds from '@glass/shared/components/hooks/useBatchedPageSeeds';
import mergeRefs from '@glass/shared/helpers/utils/mergeRefs';
import { EXAMPLES_CONTENT_WORD } from '@glass/shared/modules/content/staticVariations/contentWords/contentWordVariations';
import useContentWords from '@glass/shared/modules/content/staticVariations/contentWords/useContentWords';
import useResumeWords from '@glass/shared/modules/content/staticVariations/resumeWords/useResumeWords';
import usePageTerms from '@glass/shared/modules/content/staticVariations/usePageTerms';
import useVerbWords from '@glass/shared/modules/content/staticVariations/verbWords/useVerbWords';
import usePageData from '@glass/shared/modules/pageData/usePageData';
import createBrandResume from '@glass/shared/modules/resumes/helpers/createBrandResume';
import useTemplate from '@glass/shared/modules/resumes/hooks/useTemplate';
import randomResumeQuery from '@glass/shared/modules/resumes/queries/randomResume.graphql';
import withTrackingContext from '@glass/shared/modules/tracking/withTrackingContext';

const MAX_EDITABLE_RESUMES = 16;

const changeResumeIdx = (resumeIdx, batchedSeedsLength, delta) => {
  if (delta > 0) {
    return resumeIdx < batchedSeedsLength - delta ? resumeIdx + delta : 0;
  }
  if (delta < 0) {
    return resumeIdx >= -delta ? resumeIdx + delta : batchedSeedsLength + delta;
  }
  return resumeIdx;
};

const RandomResumeSection = React.forwardRef(
  (
    { template, children, name, pageTerm: pageTermProp, title, subtitle, isAlternatingColor, type },
    refProp,
  ) => {
    const { isFallback } = useRouter();
    const isAssignmentReady = useIsAssignmentReady();

    const { refInView, isDocumentReadyPriorityLoadedIdleInView } =
      useIsDocumentReadyPriorityLoadedIdleInView();

    const resumeWords = useResumeWords();
    const verbWords = useVerbWords();
    const contentWords = useContentWords();

    const {
      query: { [SEED_QUERY]: seedOverride },
    } = useRouter();

    const { iSlug, jSlug } = usePageTerms();

    const [resumeIdx, setResumeIdx] = useState(0);
    const batchedSeeds = useBatchedPageSeeds(MAX_EDITABLE_RESUMES);

    const seed = seedOverride && resumeIdx === 0 ? seedOverride : batchedSeeds[resumeIdx];

    const delay = !isDocumentReadyPriorityLoadedIdleInView || isFallback;
    const skip = !isAssignmentReady || delay || typeof seed !== 'string';

    // fetch random resume
    const { data, loading: resumeLoading } = useQuery(randomResumeQuery, {
      skip,
      variables: {
        seed,
        jSlug,
        iSlug,
      },
      ssr: false,
    });

    // fetch random resume template
    const { template: templateSettings, loading: templateLoading } = useTemplate({
      seedOverride: seed,
      slug: resumeIdx === 0 ? template : undefined,
      skip: delay,
      ssr: false,
    });

    const nextResumeIndex = changeResumeIdx(resumeIdx, batchedSeeds?.length, 1);
    const prefetchSeed = batchedSeeds?.[nextResumeIndex];
    const skipPrefetch = skip || resumeLoading || typeof prefetchSeed !== 'string';

    // prefetch random resume
    useQuery(randomResumeQuery, {
      skip: skipPrefetch,
      variables: {
        seed: prefetchSeed,
        jSlug,
        iSlug,
      },
      ssr: false,
    });

    // prefetch random resume template
    const { template: prefetchTemplateSettings } = useTemplate({
      seedOverride: prefetchSeed,
      skip: delay,
      ssr: false,
    });

    // prefetch the font
    useResumeFontLoader(prefetchTemplateSettings?.[FONT_STYLE_FIELD]);

    const handleNextResume = useCallback(() => {
      setResumeIdx(nextResumeIndex);
    }, [nextResumeIndex]);

    const resume = useMemo(() => {
      let filledResume = data?.randomResume;
      if (filledResume?.[RESUME_V2_FIELD] && templateSettings) {
        filledResume = {
          ...(filledResume || {}),
          [RESUME_V2_FIELD]: {
            ...(filledResume?.[RESUME_V2_FIELD] || {}),
            [TEMPLATE_FIELD_V2]: templateSettings,
          },
        };
      }
      return createBrandResume({ jSlug, iSlug })(filledResume);
    }, [data, templateSettings, jSlug, iSlug]);

    const pageTerm = pageTermProp || name; /// deprecated

    const defaultTitle = useMemo(() => {
      return (
        <span>
          {pageTerm ? (
            <span>
              <b>{pageTerm}</b> {EXAMPLES_CONTENT_WORD}
            </span>
          ) : (
            <span>
              {resumeWords.capitalized}
              {SPACE_STRING}
              <b>{EXAMPLES_CONTENT_WORD}</b>
              {contentWords.plural !== EXAMPLES_CONTENT_WORD ? (
                <span>
                  {SPACE_STRING}
                  and <b>{contentWords.plural}</b>
                </span>
              ) : (
                ''
              )}
            </span>
          )}
          {SPACE_STRING}
          you&apos;ll love{pageTerm ? '' : <span> for any&nbsp;job</span>}
        </span>
      );
    }, [pageTerm, resumeWords.capitalized, contentWords.plural]);

    const defaultSubtitle = useMemo(
      () => (
        <span>
          Use these
          {name ? (
            <b>
              {SPACE_STRING}
              {name}
            </b>
          ) : (
            ''
          )}
          {SPACE_STRING}
          to <b>easily</b> {verbWords.singular} your
          {SPACE_STRING}
          {resumeWords.singular} <b>better</b>
        </span>
      ),
      [name, verbWords.singular, resumeWords.singular],
    );

    const { templatesBasePath, isTemplateLandingPage } = usePageData();

    const ref = useMemo(() => mergeRefs(refProp, refInView), [refProp, refInView]);

    if (!delay && !templateLoading && !resumeLoading && !(resume && templateSettings)) {
      return null;
    }

    return (
      <Section
        ref={ref}
        ctaComponent={isTemplateLandingPage ? undefined : Link}
        ctaHref={isTemplateLandingPage ? undefined : templatesBasePath}
        ctaParams={isTemplateLandingPage ? undefined : null}
        ctaText="Browse $(resumeWords.singular) templates"
        isAlternatingColor={isAlternatingColor}
        subtitle={subtitle || defaultSubtitle}
        title={title || defaultTitle}
        type={type}
      >
        {children}
        <RandomResume
          loading={delay || templateLoading || resumeLoading}
          resume={resume}
          onNextResume={handleNextResume}
        />
      </Section>
    );
  },
);

RandomResumeSection.propTypes = {
  children: PropTypes.node,
  isAlternatingColor: PropTypes.bool,
  name: PropTypes.string,
  pageTerm: PropTypes.string,
  subtitle: PropTypes.string,
  template: PropTypes.string,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  type: PropTypes.string,
};

export default React.memo(withTrackingContext(RandomResumeSection, RANDOM_RESUME_SECTION_ID));
